import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts'
import { Typography, CircularProgress, Tooltip as MuiTooltip, IconButton } from '@material-ui/core'
import SettingsIcon from '@material-ui/icons/Settings'
import { useTheme } from '@material-ui/core/styles'
import useStyles from './DashboardLineChart.styles'


export const DashboardLineChart = ({
  cell,
  data,
  isProcessing,
  onConfigClick,
  subtitle = 'Estatísticas mensais comparativas dos últimos 6 meses',
  height = 320
}) => {
  const classes = useStyles()
  const theme = useTheme()

  if (!cell) return null

  // Argument X-axis property key
  const argumentKey = 'month'

  // Dynamically extract data series keys by checking keys of the first item (excluding x-axis month key)
  const seriesKeys = useMemo(() => {
    if (!data || data.length === 0) return []
    const firstItem = data[0]
    if (!firstItem) return []

    return Object.keys(firstItem).filter((key) => key !== argumentKey)
  }, [data])

  const isDataValid = useMemo(() => {
    return seriesKeys.length > 0
  }, [seriesKeys])

  // Map readable chart title according to configured temporal chart type
  const getChartTitle = () => {
    switch (cell.type) {
      case 'cadaveres_mes': return 'Cadáveres por Mês'
      case 'vestigios_setor_mes': return cell.sectorName ? `Vestígios Recebidos - ${cell.sectorName}` : 'Vestígios Recebidos no Setor'
      case 'exames_setor_mes': return cell.sectorName ? `Exames Solicitados - ${cell.sectorName}` : 'Exames Solicitados ao Setor'
      case 'exames_atribuidos_mes': return 'Meus Exames Atribuídos'
      case 'exames_publicados_mes': return 'Meus Exames Finalizados'
      default: return 'Estatísticas Temporais'
    }
  }

  const getSeriesDisplayName = (key) => {
    const fixedNames = {
      natal: 'Natal',
      mossoro: 'Mossoró',
      caico: 'Caicó',
      pdf: 'Pau dos Ferros',
      amount: 'Total',
    }
    return fixedNames[key] || key.charAt(0).toUpperCase() + key.slice(1)
  }


  const getSeriesColor = (key, index) => {
    const fixedColors = {
      natal: '#0284C7',
      mossoro: '#E9573F',
      caico: '#10B981',
      pdf: '#8B5CF6',
      amount: '#0284C7',
    }
    if (fixedColors[key]) return fixedColors[key]

    const palette = ['#0284C7', '#E9573F', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#EC4899', '#3B82F6']
    return palette[index % palette.length]
  }

  const textStyle = {
    fill: theme.palette.text.secondary || '#666',
    fontSize: '11px',
    fontFamily: theme.typography.fontFamily || 'Inter, sans-serif',
  }

  const tooltipContentStyle = {
    backgroundColor: theme.palette.background.paper || '#fff',
    border: `1px solid ${theme.palette.divider || '#ccc'}`,
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    fontFamily: theme.typography.fontFamily || 'Inter, sans-serif',
    fontSize: '12px',
  }

  return (
    <div className={classes.dashboardCell}>
      {/* Settings gear hover trigger button */}
      <MuiTooltip title="Configurar Gráfico">
        <IconButton className={classes.configButton} onClick={onConfigClick}>
          <SettingsIcon style={{ fontSize: 16 }} />
        </IconButton>
      </MuiTooltip>

      <div className={classes.chartCard}>
        {/* Loading spinner overlay */}
        {isProcessing && (
          <div className={classes.cellLoadingOverlay}>
            <CircularProgress size={24} disableShrink />
          </div>
        )}

        <Typography className={classes.chartTitle} variant="h3">
          {getChartTitle()}
        </Typography>
        <Typography className={classes.chartSubtitle} variant="body2">
          {subtitle}
        </Typography>

        <div>
          {!isProcessing && isDataValid ? (
            <div style={{ width: '100%', height }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider || '#e4e7eb'} />
                  <XAxis
                    dataKey={argumentKey}
                    tick={textStyle}
                    axisLine={{ stroke: theme.palette.divider || '#ccc' }}
                    tickLine={{ stroke: theme.palette.divider || '#ccc' }}
                  />
                  <YAxis
                    tick={textStyle}
                    axisLine={{ stroke: theme.palette.divider || '#ccc' }}
                    tickLine={{ stroke: theme.palette.divider || '#ccc' }}
                  />
                  <Tooltip contentStyle={tooltipContentStyle} />

                  {seriesKeys.map((key, idx) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      name={getSeriesDisplayName(key)}
                      stroke={getSeriesColor(key, idx)}
                      strokeWidth={2.5}
                      activeDot={{ r: 6 }}
                      dot={{ r: seriesKeys.length > 1 ? 3 : 4 }}
                    />
                  ))}

                  {seriesKeys.length > 1 && (
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      iconType="circle"
                      wrapperStyle={{
                        paddingTop: '16px',
                        fontFamily: theme.typography.fontFamily || 'Inter, sans-serif',
                        fontSize: '12px',
                        color: theme.palette.text.secondary || '#666',
                      }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className={classes.noDataContainer} style={{ height }}>
              {!isProcessing && (
                <Typography variant="body2" className={classes.noDataText}>
                  Nenhum dado válido disponível para o período selecionado.
                </Typography>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

DashboardLineChart.propTypes = {
  cell: PropTypes.object.isRequired,
  data: PropTypes.array,
  isProcessing: PropTypes.bool,
  onConfigClick: PropTypes.func.isRequired,
  subtitle: PropTypes.string,
  height: PropTypes.number,
}
